import { z } from 'zod';
import type { FilterState, FilterItem, FilterGroup } from './types';
import {
  STRING_OPERATORS,
  NUMBER_OPERATORS,
  DATE_OPERATORS,
  ENUM_OPERATORS,
} from './types';

const FilterOperatorSchema = z.union([
  z.enum(STRING_OPERATORS),
  z.enum(NUMBER_OPERATORS),
  z.enum(DATE_OPERATORS),
  z.enum(ENUM_OPERATORS),
]);

const FilterLogicSchema = z.enum(['AND', 'OR']);

const FilterConditionSchema = z.object({
  id: z.string(),
  fieldId: z.string(),
  operator: FilterOperatorSchema,
  value: z.union([z.string(), z.number(), z.array(z.string()), z.null()]),
});

const FilterItemSchema: z.ZodType<FilterItem> = z.lazy(() =>
  z.discriminatedUnion('type', [
    z.object({
      type: z.literal('condition'),
      condition: FilterConditionSchema,
    }),
    z.object({
      type: z.literal('group'),
      group: FilterGroupSchema,
    }),
  ]),
);

const FilterGroupSchema: z.ZodType<FilterGroup> = z.object({
  id: z.string(),
  logic: FilterLogicSchema,
  conditions: z.array(FilterItemSchema),
});

const FilterStateSchema = z.object({
  topLevelLogic: FilterLogicSchema,
  items: z.array(FilterItemSchema),
});

export function serialize(state: FilterState): string {
  return JSON.stringify(state);
}

export function deserialize(json: string): FilterState | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  const result = FilterStateSchema.safeParse(parsed);
  return result.success ? result.data : null;
}

export function toURLParam(state: FilterState): string {
  return encodeURIComponent(serialize(state));
}

export function fromURLParam(param: string): FilterState | null {
  let decoded: string;
  try {
    decoded = decodeURIComponent(param);
  } catch {
    return null;
  }
  return deserialize(decoded);
}

export function validate(state: unknown): state is FilterState {
  return FilterStateSchema.safeParse(state).success;
}

export { FilterStateSchema };
